import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setTokenGetter, setRefreshFn } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("bauen_access_token"));
  const [refreshTokenValue, setRefreshTokenValue] = useState(() => localStorage.getItem("bauen_refresh_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => { setTokenGetter(() => accessToken); }, [accessToken]);

  const doRefresh = useCallback(async () => {
    const rt = localStorage.getItem("bauen_refresh_token");
    if (!rt) return null;
    try {
      const resp = await api.post("/auth/refresh", { refresh_token: rt });
      const newAccess = resp.data.access_token;
      localStorage.setItem("bauen_access_token", newAccess);
      setAccessToken(newAccess);
      return newAccess;
    } catch (e) {
      localStorage.removeItem("bauen_access_token");
      localStorage.removeItem("bauen_refresh_token");
      setAccessToken(null);
      setRefreshTokenValue(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => { setRefreshFn(doRefresh); }, [doRefresh]);

  const fetchMe = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accessToken) fetchMe(); else setLoading(false);
  }, [accessToken, fetchMe]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data.requires_2fa) {
      return r.data;
    }
    const { access_token, refresh_token, user: u } = r.data;
    localStorage.setItem("bauen_access_token", access_token);
    localStorage.setItem("bauen_refresh_token", refresh_token);
    setAccessToken(access_token);
    setRefreshTokenValue(refresh_token);
    setUser(u);
    return u;
  };

  const verify2FA = async (email, code) => {
    const r = await api.post("/auth/verify-2fa", { email, code });
    const { access_token, refresh_token, user: u } = r.data;
    localStorage.setItem("bauen_access_token", access_token);
    localStorage.setItem("bauen_refresh_token", refresh_token);
    setAccessToken(access_token);
    setRefreshTokenValue(refresh_token);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { void _e; }
    localStorage.removeItem("bauen_access_token");
    localStorage.removeItem("bauen_refresh_token");
    setAccessToken(null);
    setRefreshTokenValue(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, accessToken, loading, login, verify2FA, logout, refresh: doRefresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
